import { useEffect, useState } from 'react';
import adminApi from '@/lib/adminApi';

const emptyForm = {
  title: '',
  status: 'upcoming',
  review_status: 'published',
  audition_date: '',
  art_form: '',
  city: '',
  venue: '',
  description: '',
  rules: '',
  youtube_url: '',
  image_url: '',
  display_order: 0,
  is_active: true,
};

const statusOptions = [
  { value: 'live', label: 'Live now' },
  { value: 'upcoming', label: 'Upcoming' },
  { value: 'past', label: 'Past' },
];

const reviewOptions = [
  { value: 'pending_review', label: 'Pending review' },
  { value: 'published', label: 'Published' },
  { value: 'admin_rejected', label: 'Rejected / needs changes' },
  { value: 'closed', label: 'Closed' },
];

const inviteStatusOptions = [
  { value: 'invited', label: 'Invited' },
  { value: 'accepted_invite', label: 'Accepted Invite' },
  { value: 'declined_invite', label: 'Declined Invite' },
  { value: 'audition_given', label: 'Audition Given' },
  { value: 'in_review', label: 'In Review' },
  { value: 'selected', label: 'Selected' },
  { value: 'rejected', label: 'Rejected' },
];

const AdminLiveAuditions = () => {
  const [auditions, setAuditions] = useState([]);
  const [artists, setArtists] = useState([]);
  const [invites, setInvites] = useState([]);
  const [inviteDrafts, setInviteDrafts] = useState({});
  const [counts, setCounts] = useState({});
  const [formData, setFormData] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [youtubeStatus, setYoutubeStatus] = useState(null);
  const [youtubeLoading, setYoutubeLoading] = useState(false);
  const [creatingLiveId, setCreatingLiveId] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const loadAuditions = async () => {
    setLoading(true);
    try {
      const [auditionResponse, artistResponse] = await Promise.all([
        adminApi.get('/admin/live-auditions'),
        adminApi.get('/admin/artists'),
      ]);
      setAuditions(auditionResponse.data.auditions || []);
      setInvites(auditionResponse.data.invites || []);
      setCounts(auditionResponse.data.counts || {});
      setArtists(artistResponse.data.artists || []);
    } catch (requestError) {
      setError(requestError.response?.data?.detail || 'Unable to load live auditions.');
    } finally {
      setLoading(false);
    }
  };

  const loadYoutubeStatus = async () => {
    try {
      const response = await adminApi.get('/admin/youtube/oauth/status');
      setYoutubeStatus(response.data || null);
    } catch (requestError) {
      setYoutubeStatus({ configured: false, connected: false, error: requestError.response?.data?.detail || 'Unable to check YouTube connection.' });
    }
  };

  useEffect(() => {
    loadAuditions();
    loadYoutubeStatus();
    const params = new URLSearchParams(window.location.search);
    if (params.get('youtube') === 'connected') {
      setSuccess('YouTube channel connected successfully.');
      window.history.replaceState({}, '', window.location.pathname);
    }
    if (params.get('youtube') === 'error') {
      setError(`YouTube connection failed: ${params.get('reason') || 'unknown error'}`);
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  const updateField = (field, value) => setFormData((current) => ({ ...current, [field]: value }));

  const resetForm = () => {
    setFormData(emptyForm);
    setEditingId(null);
    setError('');
  };

  const submitAudition = async (event) => {
    event.preventDefault();
    setSaving(true);
    setError('');
    try {
      const payload = { ...formData, display_order: Number(formData.display_order || 0) };
      if (editingId) {
        await adminApi.put(`/admin/live-auditions/${editingId}`, payload);
      } else {
        await adminApi.post('/admin/live-auditions', payload);
      }
      resetForm();
      await loadAuditions();
    } catch (requestError) {
      setError(requestError.response?.data?.detail || 'Unable to save live audition.');
    } finally {
      setSaving(false);
    }
  };

  const editAudition = (audition) => {
    setEditingId(audition.id);
    setFormData({
      title: audition.title || '',
      status: audition.status || 'upcoming',
      review_status: audition.review_status || 'published',
      audition_date: audition.audition_date || '',
      art_form: audition.art_form || '',
      city: audition.city || '',
      venue: audition.venue || '',
      description: audition.description || '',
      rules: audition.rules || '',
      youtube_url: audition.youtube_url || '',
      image_url: audition.image_url || '',
      display_order: audition.display_order || 0,
      is_active: Boolean(audition.is_active),
    });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const deleteAudition = async (auditionId) => {
    if (!window.confirm('Delete this live audition?')) return;
    await adminApi.delete(`/admin/live-auditions/${auditionId}`);
    await loadAuditions();
  };

  const connectYoutube = async () => {
    setYoutubeLoading(true);
    setError('');
    try {
      const response = await adminApi.get('/admin/youtube/oauth/start');
      if (response.data?.authorization_url) {
        window.location.href = response.data.authorization_url;
      } else {
        setError('Google authorization URL was not returned.');
      }
    } catch (requestError) {
      setError(requestError.response?.data?.detail || 'Unable to start YouTube connection.');
    } finally {
      setYoutubeLoading(false);
    }
  };

  const disconnectYoutube = async () => {
    if (!window.confirm('Disconnect the KalaManch YouTube channel from admin live scheduling? Existing YouTube links will stay on already-created auditions.')) return;
    setYoutubeLoading(true);
    setError('');
    setSuccess('');
    try {
      const response = await adminApi.delete('/admin/youtube/oauth/disconnect');
      setSuccess(response.data?.message || 'YouTube channel disconnected.');
      await loadYoutubeStatus();
    } catch (requestError) {
      setError(requestError.response?.data?.detail || 'Unable to disconnect YouTube channel.');
    } finally {
      setYoutubeLoading(false);
    }
  };

  const createYoutubeLive = async (auditionId) => {
    setCreatingLiveId(auditionId);
    setError('');
    setSuccess('');
    try {
      await adminApi.post(`/admin/live-auditions/${auditionId}/youtube-live`);
      setSuccess('YouTube Live created and linked to this audition.');
      await loadAuditions();
      await loadYoutubeStatus();
    } catch (requestError) {
      setError(requestError.response?.data?.detail || 'Unable to create YouTube Live.');
    } finally {
      setCreatingLiveId(null);
    }
  };

  const inviteArtist = async (auditionId) => {
    const artistId = inviteDrafts[auditionId] || '';
    if (!artistId) {
      setError('Select an artist to invite.');
      return;
    }
    setError('');
    setSuccess('');
    try {
      await adminApi.post(`/admin/live-auditions/${auditionId}/invites`, { artist_id: artistId });
      setSuccess('Artist invited to live audition.');
      setInviteDrafts((current) => ({ ...current, [auditionId]: '' }));
      await loadAuditions();
    } catch (requestError) {
      setError(requestError.response?.data?.detail || 'Unable to invite artist.');
    }
  };

  const updateInvite = async (auditionId, artistId, updates) => {
    setError('');
    setSuccess('');
    try {
      await adminApi.put(`/admin/live-auditions/${auditionId}/invites/${artistId}`, updates);
      setSuccess('Audition invite updated.');
      await loadAuditions();
    } catch (requestError) {
      setError(requestError.response?.data?.detail || 'Unable to update invite.');
    }
  };

  const deleteInvite = async (auditionId, artistId) => {
    if (!window.confirm('Remove this artist invitation?')) return;
    await adminApi.delete(`/admin/live-auditions/${auditionId}/invites/${artistId}`);
    await loadAuditions();
  };

  const invitesFor = (auditionId) => invites.filter((invite) => invite.audition_id === auditionId);

  return (
    <div className="space-y-6">
      <section className="rounded-[2rem] border border-orange-100 bg-white p-6 shadow-lg sm:p-8">
        <p className="text-sm font-semibold uppercase tracking-[0.25em] text-[#FF6F00]">Live Auditions</p>
        <div className="mt-3 flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <h2 className="text-3xl font-bold text-gray-900">Live audition management</h2>
            <p className="mt-3 max-w-3xl text-gray-600">
              Admin can create auditions directly. Collaborator-submitted auditions arrive here for review before appearing publicly.
            </p>
          </div>
          <button type="button" onClick={resetForm} className="btn-outline">Add New Audition</button>
        </div>
        <div className="mt-6 grid gap-3 sm:grid-cols-4">
          {reviewOptions.map((item) => (
            <div key={item.value} className="rounded-2xl border border-orange-100 bg-orange-50/50 p-4">
              <p className="text-sm font-semibold text-gray-600">{item.label}</p>
              <p className="mt-2 text-3xl font-black text-gray-950">{counts[item.value] || 0}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="rounded-[2rem] border border-red-100 bg-white p-6 shadow-lg sm:p-8">
        <p className="text-sm font-semibold uppercase tracking-[0.25em] text-red-600">YouTube Live Connection</p>
        <div className="mt-3 flex flex-col gap-5 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <h3 className="text-2xl font-bold text-gray-900">
              {youtubeStatus?.connected ? 'YouTube channel connected' : 'Connect KalaManch YouTube channel'}
            </h3>
            <p className="mt-2 max-w-3xl text-gray-600">
              Connect once with Google OAuth. After that, admin can create unlisted YouTube Live streams directly from approved audition records.
            </p>
            <div className="mt-4 flex flex-wrap gap-2 text-sm font-semibold">
              <span className={`rounded-full px-3 py-1 ${youtubeStatus?.configured ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                OAuth env: {youtubeStatus?.configured ? 'configured' : 'missing'}
              </span>
              <span className={`rounded-full px-3 py-1 ${youtubeStatus?.live_enabled ? 'bg-green-100 text-green-700' : 'bg-orange-100 text-orange-700'}`}>
                Live scheduling: {youtubeStatus?.live_enabled ? 'enabled' : 'disabled'}
              </span>
              <span className={`rounded-full px-3 py-1 ${youtubeStatus?.connected ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'}`}>
                Channel: {youtubeStatus?.connected ? (youtubeStatus?.channel?.title || 'connected') : 'not connected'}
              </span>
            </div>
            {youtubeStatus?.redirect_uri && (
              <p className="mt-3 break-all text-xs text-gray-500">Redirect URI: {youtubeStatus.redirect_uri}</p>
            )}
          </div>
          <div className="flex flex-wrap gap-3 lg:justify-end">
            <button type="button" onClick={connectYoutube} disabled={youtubeLoading || !youtubeStatus?.configured} className="btn-primary whitespace-nowrap disabled:bg-gray-300">
              {youtubeLoading ? 'Opening Google...' : youtubeStatus?.connected ? 'Reconnect YouTube' : 'Connect YouTube'}
            </button>
            {youtubeStatus?.connected && (
              <button type="button" onClick={disconnectYoutube} disabled={youtubeLoading} className="rounded-full border border-red-200 px-5 py-3 font-semibold text-red-600 hover:bg-red-50 disabled:opacity-60">
                Disconnect
              </button>
            )}
          </div>
        </div>
      </section>

      <form onSubmit={submitAudition} className="rounded-[2rem] border border-orange-100 bg-white p-6 shadow-lg sm:p-8">
        <div className="mb-5 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h3 className="text-2xl font-bold text-gray-900">{editingId ? 'Edit audition' : 'Add audition'}</h3>
            <p className="text-gray-600">Use YouTube Live URL now. Full YouTube Live API scheduling can be added after OAuth setup.</p>
          </div>
          {editingId && <button type="button" onClick={resetForm} className="btn-outline">Cancel Edit</button>}
        </div>

        <div className="grid gap-4 lg:grid-cols-3">
          <input value={formData.title} onChange={(event) => updateField('title', event.target.value)} placeholder="Audition title" className="rounded-2xl border border-gray-300 px-4 py-3" required />
          <input value={formData.audition_date} onChange={(event) => updateField('audition_date', event.target.value)} placeholder="Date/time" className="rounded-2xl border border-gray-300 px-4 py-3" />
          <select value={formData.status} onChange={(event) => updateField('status', event.target.value)} className="rounded-2xl border border-gray-300 px-4 py-3">
            {statusOptions.map((item) => <option key={item.value} value={item.value}>{item.label}</option>)}
          </select>
          <select value={formData.review_status} onChange={(event) => updateField('review_status', event.target.value)} className="rounded-2xl border border-gray-300 px-4 py-3">
            {reviewOptions.map((item) => <option key={item.value} value={item.value}>{item.label}</option>)}
          </select>
          <input value={formData.art_form} onChange={(event) => updateField('art_form', event.target.value)} placeholder="Art form" className="rounded-2xl border border-gray-300 px-4 py-3" />
          <input value={formData.city} onChange={(event) => updateField('city', event.target.value)} placeholder="City / online" className="rounded-2xl border border-gray-300 px-4 py-3" />
          <input value={formData.venue} onChange={(event) => updateField('venue', event.target.value)} placeholder="Venue/platform" className="rounded-2xl border border-gray-300 px-4 py-3" />
          <input value={formData.youtube_url} onChange={(event) => updateField('youtube_url', event.target.value)} placeholder="YouTube Live URL" className="rounded-2xl border border-gray-300 px-4 py-3 lg:col-span-2" />
          <p className="rounded-2xl border border-orange-100 bg-orange-50 px-4 py-3 text-sm font-semibold text-[#9A3412] lg:col-span-3">
            Collaborator-owned YouTube URLs run on the collaborator channel. Blank URL means admin can create the live on KalaManch YouTube after approval. Approve only public or unlisted YouTube Live links; private or unreviewable links should be rejected.
          </p>
          <input value={formData.image_url} onChange={(event) => updateField('image_url', event.target.value)} placeholder="Image URL" className="rounded-2xl border border-gray-300 px-4 py-3 lg:col-span-2" />
          <input type="number" value={formData.display_order} onChange={(event) => updateField('display_order', event.target.value)} placeholder="Display order" className="rounded-2xl border border-gray-300 px-4 py-3" />
        </div>

        <textarea value={formData.description} onChange={(event) => updateField('description', event.target.value)} placeholder="Audition details" className="mt-4 min-h-[110px] w-full rounded-2xl border border-gray-300 px-4 py-3" />
        <textarea value={formData.rules} onChange={(event) => updateField('rules', event.target.value)} placeholder="Rules / participation instructions" className="mt-4 min-h-[90px] w-full rounded-2xl border border-gray-300 px-4 py-3" />
        <label className="mt-4 inline-flex items-center gap-3 text-sm font-semibold text-gray-700">
          <input type="checkbox" checked={formData.is_active} onChange={(event) => updateField('is_active', event.target.checked)} />
          Active on public Live Auditions page
        </label>

        {error && <p className="mt-4 rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</p>}
        {success && <p className="mt-4 rounded-2xl border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-700">{success}</p>}
        <button type="submit" disabled={saving} className="btn-primary mt-5 disabled:bg-gray-300">
          {saving ? 'Saving...' : editingId ? 'Save Audition Changes' : 'Create Audition'}
        </button>
      </form>

      <section className="rounded-[2rem] border border-orange-100 bg-white shadow-lg">
        {loading ? (
          <div className="p-6 text-gray-500">Loading auditions...</div>
        ) : auditions.length === 0 ? (
          <div className="p-6 text-gray-500">No live auditions added yet.</div>
        ) : (
          <div className="divide-y divide-orange-100">
            {auditions.map((audition) => {
              const auditionInvites = invitesFor(audition.id);
              return (
              <article key={audition.id} className="grid gap-5 p-6 xl:grid-cols-[1fr_320px] xl:items-start">
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <h3 className="text-xl font-bold text-gray-900">{audition.title}</h3>
                    <span className={`rounded-full px-3 py-1 text-xs font-bold ${audition.review_status === 'published' ? 'bg-green-100 text-green-700' : audition.review_status === 'admin_rejected' ? 'bg-red-100 text-red-700' : 'bg-gray-200 text-gray-700'}`}>
                      {audition.review_status || 'pending_review'}
                    </span>
                    <span className="rounded-full bg-purple-100 px-3 py-1 text-xs font-bold text-[#4A148C]">{audition.status}</span>
                    <span className="rounded-full bg-orange-100 px-3 py-1 text-xs font-bold text-[#C45A00]">{audition.posted_by_role || 'admin'}</span>
                  </div>
                  <p className="mt-2 text-sm font-semibold text-gray-600">
                    {[audition.audition_date, audition.art_form, audition.venue, audition.city].filter(Boolean).join(' | ') || 'No date or venue added'}
                  </p>
                  <p className="mt-1 text-xs text-gray-500">Posted by {audition.posted_by_name || 'KalaManch Admin'}</p>
                  {audition.description && <p className="mt-2 max-w-4xl text-sm text-gray-600">{audition.description}</p>}

                  <div className="mt-5 rounded-3xl border border-purple-100 bg-purple-50/40 p-4">
                    <div className="flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
                      <div>
                        <p className="text-xs font-black uppercase tracking-[0.22em] text-[#4A148C]">Invited Artists</p>
                        <p className="mt-1 text-sm text-gray-600">Invite selected artists and publish only selected participants when ready.</p>
                      </div>
                      <div className="flex flex-col gap-2 sm:flex-row">
                        <select
                          value={inviteDrafts[audition.id] || ''}
                          onChange={(event) => setInviteDrafts((current) => ({ ...current, [audition.id]: event.target.value }))}
                          className="min-w-[220px] rounded-2xl border border-purple-100 bg-white px-4 py-2 text-sm font-semibold text-gray-900"
                        >
                          <option value="">Select artist</option>
                          {artists.map((artist) => (
                            <option key={artist.id} value={artist.id}>
                              {artist.name || 'Artist'}{artist.city ? ` - ${artist.city}` : ''}
                            </option>
                          ))}
                        </select>
                        <button type="button" onClick={() => inviteArtist(audition.id)} className="rounded-full bg-[#4A148C] px-5 py-2 text-sm font-bold text-white">
                          Invite
                        </button>
                      </div>
                    </div>

                    {auditionInvites.length === 0 ? (
                      <p className="mt-4 rounded-2xl bg-white px-4 py-3 text-sm text-gray-500">No artists invited yet.</p>
                    ) : (
                      <div className="mt-4 grid gap-3">
                        {auditionInvites.map((invite) => (
                          <div key={invite.id || `${invite.audition_id}-${invite.artist_id}`} className="rounded-2xl bg-white p-3 ring-1 ring-purple-100">
                            <div className="flex flex-wrap items-center justify-between gap-3">
                              <div>
                                <p className="font-bold text-gray-950">{invite.artist_name || 'Artist'}</p>
                                <p className="text-xs font-semibold text-gray-500">{invite.artist_art_form || 'Art form not added'}</p>
                              </div>
                              <span className="rounded-full bg-purple-50 px-3 py-1 text-xs font-bold text-[#4A148C]">{(invite.status || 'invited').replaceAll('_', ' ')}</span>
                            </div>
                            <div className="mt-3 grid gap-2 sm:grid-cols-[1fr_auto_auto]">
                              <select
                                value={invite.status || 'invited'}
                                onChange={(event) => updateInvite(audition.id, invite.artist_id, { status: event.target.value, public_visible: Boolean(invite.public_visible) })}
                                className="rounded-2xl border border-gray-200 px-3 py-2 text-sm font-semibold"
                              >
                                {inviteStatusOptions.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
                              </select>
                              <button
                                type="button"
                                onClick={() => updateInvite(audition.id, invite.artist_id, { status: invite.status || 'invited', public_visible: !invite.public_visible })}
                                disabled={(invite.status || '') !== 'selected'}
                                className="rounded-full border border-emerald-200 px-4 py-2 text-xs font-bold text-emerald-700 disabled:border-gray-200 disabled:text-gray-400"
                              >
                                {invite.public_visible ? 'Public selected' : 'Make public'}
                              </button>
                              <button type="button" onClick={() => deleteInvite(audition.id, invite.artist_id)} className="rounded-full border border-red-200 px-4 py-2 text-xs font-bold text-red-600">
                                Remove
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
                <div className="flex flex-wrap gap-3 lg:justify-end">
                  <button
                    type="button"
                    onClick={() => createYoutubeLive(audition.id)}
                    disabled={!youtubeStatus?.connected || !youtubeStatus?.live_enabled || creatingLiveId === audition.id}
                    className="rounded-full border border-red-200 px-5 py-2 font-semibold text-red-600 hover:bg-red-50 disabled:border-gray-200 disabled:text-gray-400"
                  >
                    {creatingLiveId === audition.id ? 'Creating Live...' : audition.youtube_url ? 'Create New Live' : 'Create YouTube Live'}
                  </button>
                  <button type="button" onClick={() => editAudition(audition)} className="btn-outline">Edit</button>
                  <button type="button" onClick={() => deleteAudition(audition.id)} className="rounded-full border border-red-200 px-5 py-2 font-semibold text-red-600 hover:bg-red-50">Delete</button>
                </div>
              </article>
              );
            })}
          </div>
        )}
      </section>
    </div>
  );
};

export default AdminLiveAuditions;
